import React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle } from 'lucide-react';
import { formatPhoneForDisplay } from '../lib/phone';

/**
 * DuplicateCustomerModal — appears when the shop tries to add a
 * customer whose phone or email already matches an existing record
 * on the same shop.
 *
 * Called by the Diary "+ New booking" flow and the Clients "+ Add"
 * flow. Public bookings do NOT show this modal — the RPC silently
 * re-uses the existing record for a repeat customer.
 *
 * Props:
 *   existing:      the pre-existing customer row
 *   matchedField:  'phone' | 'email' — which field triggered the match
 *   onUseExisting: called when the shop picks "Use existing record"
 *   onEdit:        called when the shop picks "Change my entry"
 */

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const DuplicateCustomerModal = ({
  existing,
  matchedField,
  onUseExisting,
  onEdit,
}) => {
  const fieldLabel = matchedField === 'email' ? 'email' : 'number';
  const matchedValue = matchedField === 'email'
    ? existing?.email
    : formatPhoneForDisplay(existing?.phone);
  const lastVisit = fmtDate(existing?.last_visit);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onEdit?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onEdit]);

  return createPortal(
    <div
      className="confirm-backdrop"
      role="dialog"
      aria-modal="true"
      data-testid="duplicate-customer-modal"
      onClick={(e) => {
        // Prevent this click from bubbling to any outer modal-backdrop
        // that owns the parent modal (e.g. NewBookingModal / AddClientModal),
        // which would otherwise close it and drop the user's form entries.
        e.stopPropagation();
        if (e.target === e.currentTarget) onEdit?.();
      }}
    >
      <div className="confirm-card" style={{ maxWidth: 460 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertCircle size={20} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ minWidth: 0 }}>
            <h3 className="confirm-title" data-testid="duplicate-title">
              This {fieldLabel} already belongs to {existing?.name || 'an existing client'}
            </h3>
            <p className="confirm-body" data-testid="duplicate-body">
              A client with this {fieldLabel} is already on your list
              {matchedValue ? <> — <strong>{matchedValue}</strong></> : null}
              {lastVisit ? <> · last visit {lastVisit}</> : null}.
              Use the existing record so the booking history stays in one place, or
              go back and change what you entered.
            </p>
          </div>
        </div>
        <div className="confirm-actions">
          <button
            type="button"
            className="btn ghost"
            onClick={onEdit}
            data-testid="duplicate-edit"
          >Change my entry</button>
          <button
            type="button"
            className="btn primary"
            onClick={onUseExisting}
            data-testid="duplicate-use-existing"
          >Use existing record</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DuplicateCustomerModal;
